import type { DiscountFigures } from "~/components/demand-2/demand/DiscountsBreakdown"
import MarkdownVisibilityNote from "~/components/demand-2/demand/MarkdownVisibilityNote"
import { signedPct, signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { useState } from "react"

import {
  AnswerPicker,
  useSetFramingAnswer,
  useSetFramingAnswerForAllSlices,
} from "~/components/demand-2/shared/FramingAnswerPicker"
import { useSalesSlices } from "~/components/demand-2/shared/useSalesSlices"
import { useScopedFramingAnswer } from "~/components/demand-2/shared/useScopedFramingAnswer"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { STRATEGY_REQUIRES } from "~/components/demand-2/shared/strategyPermission"
import { formatCount, formatList } from "~/utils/formatting"
import {
  effectiveAnswerFor,
  framingOptionLabel,
  type FramingAnswers,
  type FramingSlice,
  isAllSlice,
  parseFramingAnswer,
  scopeNode,
  scopeWriteKey,
  type AnswerScope,
  type FramingDescribeContext,
  type FramingNode,
  WHOLE_COLLECTION_NODE,
} from "~/utils/demandFraming"
import { lockedQuestionReason } from "~/utils/demandFramingKinds"

/** The combinations whose own discounts answer a collection-wide `next` would replace. */
const overriddenSlices = (
  framing: FramingAnswers,
  slices: readonly { slice: FramingSlice; label: string }[],
  next: string,
): string[] => {
  const picked = parseFramingAnswer(next)?.optionId
  return slices
    .filter(({ slice }) => {
      const own = effectiveAnswerFor(framing, "discounts", { kind: "whole", slice })
      return !own.inherited && parseFramingAnswer(own.answer)?.optionId !== picked
    })
    .map(({ label }) => label)
}

const MAX_LISTED = 3

const overrideDescription = (labels: string[]): string => {
  const shown = labels.slice(0, MAX_LISTED)
  const rest = labels.length - shown.length
  const names = formatList(rest > 0 ? [...shown, `${rest} more`] : shown)
  const verb =
    labels.length === 1 ? "answers differently on its own" : "answer differently on their own"
  return `${formatCount(labels.length, "combination")} ${verb}: ${names}. Confirming applies this answer to every combination.`
}

/** The `discounts` question, via the same picker and store as the season rate and Product mix. */
const DiscountsAnswer = ({
  node: nodeProp = WHOLE_COLLECTION_NODE,
  scope: scopeProp,
  figures,
}: {
  /** Falls back to the collection's answer. */
  node?: FramingNode
  /** Overrides `node`, e.g. one variant from the Total page. */
  scope?: AnswerScope
  figures: DiscountFigures
}) => {
  const read = useScopedFramingAnswer("discounts", nodeProp, scopeProp)
  const salesSlices = useSalesSlices()
  const set = useSetFramingAnswer(read?.collectionId ?? 0)
  const setForAll = useSetFramingAnswerForAllSlices()
  // A collection pick that would replace combinations' own answers, awaiting confirmation.
  const [pending, setPending] = useState<{ answer: string; overridden: string[] } | null>(null)
  if (read == null) return null
  const { collectionId, kind, scope, framing, answer, inherited, isDefault } = read
  // NOOS measures on exactly what the baseline reads (own sales or matches), not on comparables.
  const ownSales = kind === "noos"
  const lockedReason = kind == null ? undefined : lockedQuestionReason(kind, "discounts")
  const node = scopeNode(scope)
  const isCollection = scope.kind === "whole" && isAllSlice(scope.slice)
  const hasOwnSlices = (framing.sliceAnswers ?? []).some(
    (r) => r.question === "discounts" && r.variantId === "",
  )
  // The combinations' own answers can't be compared until they load, so the pick waits.
  const slicesLoading = isCollection && hasOwnSlices && salesSlices == null
  // A collection answer covers every combination; replacing a different own answer asks first.
  const write = (next: string | null) => {
    if (slicesLoading) return
    if (!isCollection || next == null || !hasOwnSlices) {
      set.mutate({ collectionId, question: "discounts", answer: next, ...scopeWriteKey(scope) })
      return
    }
    const overridden = overriddenSlices(framing, salesSlices?.slices ?? [], next)
    if (overridden.length > 0) setPending({ answer: next, overridden })
    else setForAll.mutate({ collectionId, question: "discounts", answer: next })
  }
  const context: FramingDescribeContext = {
    discounts: {
      nodeName: node.groupKey === "" ? undefined : node.groupValue,
      ownSales,
      comparableSales: figures.comparableSales,
      discountedSales: figures.discountedSales,
      impactUnits: figures.impactUnits,
    },
  }
  // So the line under an option and the figure after picking it agree.
  const impactOf = (optionId: string): React.ReactNode => {
    if (figures.comparableSales <= 0) return null
    if (optionId === "maintain") {
      return <span className="paragraph-sm text-fg-subtle">No change to the plan</span>
    }
    return (
      <span className="paragraph-sm text-fg-subtle">
        <span className="text-fg-error-icon">
          {signedUnits(figures.impactUnits)} units ·{" "}
          {signedPct(figures.impactUnits / figures.comparableSales)}
        </span>{" "}
        {ownSales ? "of the baseline reorders" : "of the comparable reorders"}
      </span>
    )
  }
  const pendingLabel = pending ? framingOptionLabel("discounts", pending.answer) : null
  return (
    <>
      <AnswerPicker
        collectionId={collectionId}
        question="discounts"
        answer={answer}
        scope={scope}
        inherited={inherited}
        isDefault={isDefault}
        describeContext={context}
        onWrite={write}
        pending={set.isPending || setForAll.isPending || slicesLoading}
        optionFooter={impactOf}
        optionDisabled={lockedReason == null ? undefined : () => lockedReason}
        underOption={
          isCollection ? (optionId) => <MarkdownVisibilityNote optionId={optionId} /> : undefined
        }
      />
      <ConfirmationDialog
        open={pending != null}
        onOpenChange={(open: boolean) => {
          if (!open) setPending(null)
        }}
        requires={STRATEGY_REQUIRES}
        title={`Use “${pendingLabel ?? ""}” for every ${salesSlices?.noun ?? "combination"}?`}
        confirmText="Use for all"
        description={overrideDescription(pending?.overridden ?? [])}
        onConfirm={() => {
          if (pending)
            setForAll.mutate({ collectionId, question: "discounts", answer: pending.answer })
          setPending(null)
        }}
      />
    </>
  )
}

export default DiscountsAnswer
